import {
  erc20RecoveryAbi,
  prepareRecoveryTransfer,
  preflightRecovery,
  type RecoveryPreflight,
  type RecoveryTransfer,
} from '@hoodmorning/chain';
import { robinhoodChain as chainConfig } from '@hoodmorning/config';
import type { Address, Hex } from '@hoodmorning/contracts';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createWalletClient,
  custom,
  defineChain,
  formatUnits,
  getAddress,
  isAddress,
  parseUnits,
} from 'viem';

import { validateRecoveryRpcUrl } from './walletSafety.js';
import { DeploymentRecovery } from './DeploymentRecovery.js';
import { createRecoveryClient } from './recoveryRpc.js';
import { assertWithdrawalHistoryResolved, importWithdrawalHash, observeWithdrawal, readWithdrawalHistory, submitWithdrawal, WithdrawalHashPersistenceError, type WithdrawalRecord } from './withdrawal.js';

const robinhood = defineChain({
  id: chainConfig.id,
  name: chainConfig.name,
  nativeCurrency: chainConfig.nativeCurrency,
  rpcUrls: { default: { http: [...chainConfig.rpcUrls] } },
  blockExplorers: {
    default: { name: 'Robinhood Chain Explorer', url: chainConfig.blockExplorerUrl },
  },
});

type Preview = {
  transfer: RecoveryTransfer;
  balance: bigint;
  decimals: number;
  symbol: string;
  previousAttemptId: string | null;
};

function Field(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <label className="field">
      <span>{props.label}</span>
      <input
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        placeholder={props.placeholder}
        disabled={props.disabled}
        spellCheck={false}
      />
    </label>
  );
}

export function App() {
  const [rpcUrl, setRpcUrl] = useState<string>(chainConfig.rpcUrls[0]);
  const [connectedOwner, setConnectedOwner] = useState<Address | null>(null);
  const [walletChainId, setWalletChainId] = useState<number | null>(null);
  const [vault, setVault] = useState('');
  const [recipient, setRecipient] = useState('');
  const [assetKind, setAssetKind] = useState<'NATIVE' | 'ERC20'>('NATIVE');
  const [token, setToken] = useState('');
  const [tokenDecimals, setTokenDecimals] = useState('18');
  const [amount, setAmount] = useState('');
  const [preflight, setPreflight] = useState<RecoveryPreflight | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [status, setStatus] = useState('Connect the current owner wallet to begin.');
  const [txHash, setTxHash] = useState<Hex | null>(null);
  const [busy, setBusy] = useState(false);
  const [withdrawalHistory, setWithdrawalHistory] = useState<WithdrawalRecord[]>([]);
  const [unsavedAcknowledgements, setUnsavedAcknowledgements] = useState<WithdrawalRecord[]>([]);
  const [withdrawalHash, setWithdrawalHash] = useState('');
  const [journalError, setJournalError] = useState(false);
  const inFlight = useRef(false);
  const selection = JSON.stringify({ rpcUrl, connectedOwner, vault, recipient, assetKind, token, tokenDecimals, amount });
  const liveSelection = useRef(selection); liveSelection.current = selection;
  const walletRevision = useRef(0);
  function selectionGuard() {
    const saved = selection, revision = walletRevision.current;
    return () => { if (liveSelection.current !== saved || walletRevision.current !== revision) throw new Error('Recovery selection changed; verify and preview again.'); };
  }
  useEffect(() => { setPreview(null); }, [selection]);
  useEffect(() => {
    function refresh() {
      setWithdrawalHistory([]); setJournalError(false); setTxHash(null);
      if (!isAddress(vault)) return;
      try {
        const records = readWithdrawalHistory(localStorage, vault); setWithdrawalHistory(records);
        setTxHash(records.at(-1)?.replacementHash ?? records.at(-1)?.hash ?? null);
      } catch { setJournalError(true); setStatus('Saved withdrawal journal is unreadable. Sending is blocked; preserve browser storage and check wallet activity.'); }
    }
    refresh(); window.addEventListener('storage', refresh);
    return () => window.removeEventListener('storage', refresh);
  }, [vault]);
  useEffect(() => {
    const changed = () => { walletRevision.current++; setConnectedOwner(null); setWalletChainId(null); setPreview(null); setPreflight(null); setStatus('Wallet changed. Reconnect and verify again.'); };
    const provider = window.ethereum; provider?.on?.('accountsChanged', changed); provider?.on?.('chainChanged', changed);
    return () => { walletRevision.current++; provider?.removeListener?.('accountsChanged', changed); provider?.removeListener?.('chainChanged', changed); };
  }, []);

  // Keep rendering while the user edits an incomplete URL. Actions validate the
  // selected URL before using this retained client; it is never a fallback RPC.
  const lastClient = useRef(createRecoveryClient(chainConfig.rpcUrls[0]));
  const publicClient = useMemo(() => {
    try { validateRecoveryRpcUrl(rpcUrl); lastClient.current = createRecoveryClient(rpcUrl); } catch { /* incomplete form input */ }
    return lastClient.current;
  }, [rpcUrl]);

  async function connectOwner() {
    if (window.ethereum === undefined) {
      setStatus('No injected EIP-1193 wallet was found. Install/open your owner wallet; never paste a key.');
      return;
    }
    setBusy(true);
    try {
      const wallet = createWalletClient({ chain: robinhood, transport: custom(window.ethereum) });
      const [address] = await wallet.requestAddresses();
      if (address === undefined) throw new Error('Wallet returned no account');
      setConnectedOwner(getAddress(address));
      const chainId = await wallet.getChainId();
      setWalletChainId(chainId);
      setStatus(
        chainId === robinhood.id
          ? 'Owner wallet connected. Run vault verification.'
          : `Wallet is on chain ${chainId}; switch it to Robinhood Chain ${chainConfig.id} before submission.`,
      );
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Wallet connection failed');
    } finally {
      setBusy(false);
    }
  }

  async function runPreflight(): Promise<RecoveryPreflight | null> {
    const assertCurrent = selectionGuard();
    setPreview(null);
    if (connectedOwner === null || !isAddress(vault)) {
      setStatus('Connect the owner and enter a valid vault address first.');
      return null;
    }
    setBusy(true);
    try {
      validateRecoveryRpcUrl(rpcUrl);
      const result = await preflightRecovery(
        publicClient,
        getAddress(vault),
        connectedOwner,
      );
      assertCurrent();
      setPreflight(result);
      setStatus(
        result.canSubmit
          ? 'Verified: exact SMA-B owner-direct ABI and pinned infrastructure passed.'
          : `Submission locked: ${result.failures.join(', ')}`,
      );
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Vault preflight failed';
      setPreflight(null);
      setStatus(`Submission locked: ${message}`);
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function buildPreview() {
    const assertCurrent = selectionGuard();
    if (preflight?.canSubmit !== true || connectedOwner === null) {
      setStatus('Run and pass vault verification before creating a transfer.');
      return;
    }
    if (!isAddress(vault) || !isAddress(recipient)) {
      setStatus('Vault and recipient must be valid addresses.');
      return;
    }
    const decimals = Number(tokenDecimals);
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 255) {
      setStatus('Token decimals must be an integer from 0 to 255.');
      return;
    }

    setBusy(true);
    try {
      validateRecoveryRpcUrl(rpcUrl);
      const baseUnits = parseUnits(amount, assetKind === 'NATIVE' ? 18 : decimals);
      const history = readWithdrawalHistory(localStorage, vault), previousAttemptId = history.at(-1)?.attemptId ?? null;
      await assertWithdrawalHistoryResolved(publicClient, localStorage, vault);
      if ((readWithdrawalHistory(localStorage, vault).at(-1)?.attemptId ?? null) !== previousAttemptId) throw new Error('Withdrawal history changed; prepare again');
      assertCurrent();
      const asset =
        assetKind === 'NATIVE'
          ? ({ kind: 'NATIVE' } as const)
          : isAddress(token)
            ? ({ kind: 'ERC20', token: getAddress(token) } as const)
            : null;
      if (asset === null) throw new Error('Enter a valid ERC-20 contract address');
      const transfer = prepareRecoveryTransfer({
        vault: getAddress(vault),
        recipient: getAddress(recipient),
        asset,
        amount: baseUnits,
      });
      const balance =
        asset.kind === 'NATIVE'
          ? await publicClient.getBalance({ address: getAddress(vault) })
          : await publicClient.readContract({
              address: asset.token,
              abi: erc20RecoveryAbi,
              functionName: 'balanceOf',
              args: [getAddress(vault)],
            });
      if (baseUnits > balance) throw new Error('Amount exceeds the vault balance');

      let symbol = asset.kind === 'NATIVE' ? 'ETH' : 'ERC20';
      if (asset.kind === 'ERC20') {
        try {
          symbol = await publicClient.readContract({
            address: asset.token,
            abi: erc20RecoveryAbi,
            functionName: 'symbol',
          });
        } catch {
          symbol = 'ERC20';
        }
      }
      assertCurrent();
      setPreview({ transfer, balance, decimals: asset.kind === 'NATIVE' ? 18 : decimals, symbol, previousAttemptId });
      setStatus('Preview ready. Verify every address and amount, then simulate.');
    } catch (error) {
      setPreview(null);
      setStatus(error instanceof Error ? error.message : 'Could not prepare transfer');
    } finally {
      setBusy(false);
    }
  }

  async function simulateAndSubmit() {
    if (inFlight.current) return;
    const assertCurrent = selectionGuard();
    if (preview === null || connectedOwner === null || preflight?.canSubmit !== true) {
      setStatus('A verified preview is required.');
      return;
    }
    if (window.ethereum === undefined) return;
    inFlight.current = true;
    // A preview authorizes only this attempt. Never restore it after an error.
    const reviewed = preview; setPreview(null);
    setBusy(true);
    try {
      validateRecoveryRpcUrl(rpcUrl);
      if (!navigator.locks) throw new Error('Durable browser locking is required before withdrawal');
      const record = await submitWithdrawal({ transfer: reviewed.transfer, previousAttemptId: reviewed.previousAttemptId,
        owner: connectedOwner, provider: window.ethereum, client: publicClient, storage: localStorage, locks: navigator.locks, assertCurrent });
      assertCurrent();
      setWithdrawalHistory(readWithdrawalHistory(localStorage, record.intent.vault)); setTxHash(record.hash);
      setStatus(`Submitted ${record.hash}. Use the read-only withdrawal check to verify the receipt and transfer effect. Do not send again while the outcome is unknown.`);
    } catch (error) {
      // Wallet acknowledgement is evidence for its original attempt, even when
      // the current wallet/form or browser storage can no longer be read.
      if (error instanceof WithdrawalHashPersistenceError) {
        const acknowledged = error.acknowledgedRecord;
        setUnsavedAcknowledgements(records => [...records.filter(record => record.attemptId !== acknowledged.attemptId), acknowledged]);
      }
      try {
        assertCurrent(); const history = readWithdrawalHistory(localStorage, reviewed.transfer.vault), record = history.at(-1);
        setWithdrawalHistory(history); setTxHash(error instanceof WithdrawalHashPersistenceError ? error.transactionHash : record?.hash ?? null);
        if (error instanceof WithdrawalHashPersistenceError) setWithdrawalHash(error.transactionHash);
        setStatus(`${error instanceof Error ? error.message : 'Simulation/submission failed'}. ${record?.phase === 'not-broadcast' ? 'The wallet send was never invoked. Explicitly prepare and review a new attempt.' : record ? 'A saved withdrawal may have been sent. Reconcile its original hash before another send.' : 'No withdrawal was reserved. Prepare a fresh preview.'}`);
      } catch { /* Keep stale completions from overwriting the new context. */ }
    } finally {
      inFlight.current = false; setBusy(false);
    }
  }

  async function reconcileWithdrawal(importSelectedHash: boolean) {
    if (inFlight.current || !isAddress(vault)) return;
    const assertCurrent = selectionGuard(); inFlight.current = true; setBusy(true); setPreview(null);
    try {
      validateRecoveryRpcUrl(rpcUrl);
      if (importSelectedHash) {
        if (!navigator.locks) throw new Error('Durable browser locking is required');
        const imported = await importWithdrawalHash({ client: publicClient, storage: localStorage, locks: navigator.locks, vault, transactionHash: withdrawalHash, assertCurrent });
        setUnsavedAcknowledgements(records => records.filter(record => record.attemptId !== imported.attemptId));
      }
      const history = readWithdrawalHistory(localStorage, vault), record = history.at(-1);
      if (!record) throw new Error('No saved withdrawal');
      assertCurrent(); setWithdrawalHistory(history); setTxHash(record.replacementHash ?? record.hash);
      const observed = await observeWithdrawal(publicClient, record); assertCurrent();
      setStatus(observed.status === 'token-transaction-observed'
        ? `Recovery transaction observed in canonical block ${observed.receipt.blockNumber}. Token delivery is not verified: the token may charge fees, return false or behave differently. Check the recipient’s balance and token details before considering another withdrawal. Nothing is resent automatically; a new withdrawal requires a separate preview.`
        : observed.status === 'transfer-observed'
        ? `Recovery transfer observed in canonical block ${observed.receipt.blockNumber}. This is not a finality guarantee. A new withdrawal requires a separate preview.`
        : observed.status === 'self-replacement-observed'
        ? `Same-nonce owner self-transaction observed in canonical block ${observed.receipt.blockNumber}. It consumed the saved nonce, but this app cannot prove that delegated or contract code caused no recovery effects. No new withdrawal is authorized; verify vault balances and obtain support.`
        : observed.status === 'self-replacement-reverted'
        ? `Same-nonce owner self-transaction reverted in canonical block ${observed.receipt.blockNumber}. Its execution reverted and the saved nonce was consumed. A new withdrawal still requires a separate preview and confirmation.`
        : 'The selected withdrawal transaction reverted. A new withdrawal requires a separate preview.');
    } catch (error) {
      try { assertCurrent(); setStatus(`Withdrawal unresolved: ${error instanceof Error ? error.message : 'receipt/effect unavailable'}. No resend is authorized; check again or verify the original hash from the owner wallet.`); }
      catch { /* stale completion */ }
    } finally { inFlight.current = false; setBusy(false); }
  }

  return (
    <main>
      <header>
        <p className="eyebrow">Independent, owner-controlled withdrawal</p>
        <h1>HoodMorning Recovery</h1>
        <p className="lede">
          This static app talks only to your chosen Robinhood Chain RPC and injected wallet. It never
          asks for a private key, seed phrase, OTP, or HoodMorning login.
        </p>
      </header>

      <section className="warning" aria-label="Safety warning">
        <p>Recovery network: {chainConfig.name} ({chainConfig.id}).</p>
        <strong>Fail-closed by design.</strong> Submission stays locked unless chain {chainConfig.id}, the current
        owner, SMA-B account ID, EntryPoint, implementation reference, and pinned bytecode all verify.
      </section>

      <section className="warning" aria-label="Activity on other apps and devices">
        <strong>Reconcile known pending or unknown activity before a new transfer.</strong>
        <p>This page cannot read another app's saved transactions or coordinate other origins or devices.
          Check known pending or unknown transactions in the originating app or your owner wallet;
          use available hashes for read-only receipt checks. A timeout, missing receipt, balance or
          matching latest/pending nonce does not prove that an earlier transfer was never sent.</p>
        <p>A new withdrawal is a separately authorised transfer, not a retry or resolution of that activity.
          Existing automatic orders may continue running; opening recovery does not cancel them.
          This warning does not require HoodMorning login or API access.</p>
      </section>

      <section className="card">
        <div className="section-heading">
          <div><span>1</span><h2>Connect the current owner</h2></div>
          <button onClick={connectOwner} disabled={busy}>Connect wallet</button>
        </div>
        <dl>
          <div><dt>Connected owner</dt><dd>{connectedOwner ?? 'Not connected'}</dd></div>
          <div><dt>Wallet chain</dt><dd>{walletChainId ?? 'Unknown'}</dd></div>
        </dl>
      </section>

      <section className="card">
        <div className="section-heading"><div><span>2</span><h2>Verify account state</h2></div></div>
        <Field
          label="Independent RPC URL"
          value={rpcUrl}
          onChange={(value) => { setRpcUrl(value); setPreflight(null); setPreview(null); }}
        />
        <Field
          label="Vault address"
          value={vault}
          onChange={(value) => { setVault(value); setPreflight(null); setPreview(null); }}
          placeholder="0x…"
        />
        <button className="primary" onClick={runPreflight} disabled={busy || connectedOwner === null}>
          Run verification
        </button>
        {preflight !== null && (
          <dl className={preflight.canSubmit ? 'verified' : 'blocked'}>
            <div><dt>Account ID</dt><dd>{preflight.accountId ?? 'Unreadable'}</dd></div>
            <div><dt>Onchain owner</dt><dd>{preflight.onchainOwner ?? 'Unreadable'}</dd></div>
            <div><dt>Authorization</dt><dd>{preflight.authorizationPath}</dd></div>
            <div><dt>Submission</dt><dd>{preflight.canSubmit ? 'Unlocked' : 'Locked'}</dd></div>
          </dl>
        )}
      </section>

      <section className="card">
        <DeploymentRecovery owner={connectedOwner} vault={vault} rpcUrl={rpcUrl} client={publicClient} onDeployed={() => { setPreflight(null); setPreview(null); setStatus('Deployment observed. Run account verification before preparing a separate withdrawal.'); }} />
        <div className="section-heading"><div><span>3</span><h2>Prepare one transfer</h2></div></div>
        <Field label="Recipient" value={recipient} onChange={setRecipient} placeholder="0x…" />
        <label className="field">
          <span>Asset</span>
          <select value={assetKind} onChange={(event) => setAssetKind(event.target.value as 'NATIVE' | 'ERC20')}>
            <option value="NATIVE">Native ETH</option>
            <option value="ERC20">Arbitrary ERC-20</option>
          </select>
        </label>
        {assetKind === 'ERC20' && (
          <div className="two-column">
            <Field label="ERC-20 contract" value={token} onChange={setToken} placeholder="0x…" />
            <Field label="Token decimals" value={tokenDecimals} onChange={setTokenDecimals} />
          </div>
        )}
        <Field label="Amount" value={amount} onChange={setAmount} placeholder="0.0" />
        <button className="primary" onClick={buildPreview} disabled={busy || journalError || preflight?.canSubmit !== true}>
          Preview transfer
        </button>
      </section>

      {unsavedAcknowledgements.map(record => <section key={record.attemptId} className="card warning" aria-label="Unsaved withdrawal acknowledgement">
        <h2>Preserve this acknowledged withdrawal hash</h2>
        <p>The wallet returned this hash, but saving it failed. These details belong to the original withdrawal, even if the selected vault or wallet has changed. Copy the hash before closing or reloading this page. Do not resend; select the original vault and verify this hash with the read-only withdrawal check.</p>
        <dl>
          <div><dt>Original vault</dt><dd>{record.intent.vault}</dd></div>
          <div><dt>Original owner</dt><dd>{record.owner}</dd></div>
          <div><dt>Recipient</dt><dd>{record.intent.recipient}</dd></div>
          <div><dt>Asset</dt><dd>{record.intent.asset.kind === 'NATIVE' ? 'Native ETH' : record.intent.asset.token}</dd></div>
          <div><dt>Amount (base units)</dt><dd>{record.intent.amount}</dd></div>
          <div><dt>Owner nonce</dt><dd>{record.transaction.nonce}</dd></div>
          <div><dt>Acknowledged hash</dt><dd>{record.hash}</dd></div>
        </dl>
        <a className="explorer" href={`${chainConfig.blockExplorerUrl}/tx/${record.hash}`} target="_blank" rel="noreferrer">Open original withdrawal in explorer</a>
      </section>)}

      {withdrawalHistory.length > 0 && <section className="card">
        <h2>Saved withdrawal</h2>
        <p>Keep this browser's storage until reconciliation is complete. A timeout, missing receipt or changed wallet never proves that a withdrawal was not sent.</p>
        <dl>
          <div><dt>Owner</dt><dd>{withdrawalHistory.at(-1)!.owner}</dd></div>
          <div><dt>Recipient</dt><dd>{withdrawalHistory.at(-1)!.intent.recipient}</dd></div>
          <div><dt>Asset</dt><dd>{withdrawalHistory.at(-1)!.intent.asset.kind === 'NATIVE' ? 'Native ETH' : (withdrawalHistory.at(-1)!.intent.asset as { token: string }).token}</dd></div>
          <div><dt>Amount (base units)</dt><dd>{withdrawalHistory.at(-1)!.intent.amount}</dd></div>
          <div><dt>Owner nonce</dt><dd>{withdrawalHistory.at(-1)!.transaction.nonce}</dd></div>
          <div><dt>Original hash / state</dt><dd>{withdrawalHistory.at(-1)!.hash ?? withdrawalHistory.at(-1)!.phase}</dd></div>
          {withdrawalHistory.at(-1)!.replacementHash && <div><dt>Replacement</dt><dd>{withdrawalHistory.at(-1)!.replacementReason}: {withdrawalHistory.at(-1)!.replacementHash}</dd></div>}
        </dl>
        {withdrawalHistory.at(-1)!.phase !== 'not-broadcast' && <>
          <button disabled={busy} onClick={() => void reconcileWithdrawal(false)}>Read-only withdrawal transaction check</button>
          <Field label="Original or same-nonce replacement transaction hash from your owner wallet" value={withdrawalHash} onChange={setWithdrawalHash} />
          <button disabled={busy || !withdrawalHash} onClick={() => void reconcileWithdrawal(true)}>Verify selected hash and check withdrawal (no send)</button>
        </>}
        <details><summary>Preserved withdrawal history ({withdrawalHistory.length})</summary><pre>{JSON.stringify(withdrawalHistory, null, 2)}</pre></details>
      </section>}

      {preview !== null && (
        <section className="card preview">
          <div className="section-heading"><div><span>4</span><h2>Review and submit</h2></div></div>
          <dl>
            <div><dt>Vault</dt><dd>{preview.transfer.vault}</dd></div>
            <div><dt>Target contract</dt><dd>{preview.transfer.call.to}</dd></div>
            <div><dt>Recipient</dt><dd>{preview.transfer.recipient}</dd></div>
            <div><dt>Amount</dt><dd>{formatUnits(preview.transfer.amount, preview.decimals)} {preview.symbol}</dd></div>
            <div><dt>Vault balance</dt><dd>{formatUnits(preview.balance, preview.decimals)} {preview.symbol}</dd></div>
            <div><dt>Action</dt><dd>{preview.transfer.description}</dd></div>
          </dl>
          <p>One attempt only. For user-selected ERC-20 tokens we verify the exact transaction’s inclusion, not recipient delivery. Token logs and whole-block balance changes cannot prove what this transaction delivered.</p>
          <button className="danger" onClick={simulateAndSubmit} disabled={busy || journalError}>
            Simulate, then ask owner wallet to submit
          </button>
        </section>
      )}

      <p className="status" role="status">{busy ? 'Working… ' : ''}{status}</p>
      {txHash !== null && (
        <a className="explorer" href={`${chainConfig.blockExplorerUrl}/tx/${txHash}`} target="_blank" rel="noreferrer">
          Open transaction in explorer
        </a>
      )}

      <footer>
        Source-build this page and verify its checksum before emergency use. The owner wallet needs ETH
        for gas. This build supports only an injected current-owner wallet; it does not connect a Privy
        embedded signer or export keys. An embedded owner cannot use an unrelated external wallet here.
        Separately verified owner rotation to an external current owner is required for that recovery
        path. Never enter a seed phrase or private key.
      </footer>
    </main>
  );
}
